/**
 * What a list's filter names that is gone — the `X-Query-Warnings` header
 * (keepr-api § KQL "What a filter names that is gone", utils/queryWarnings.js;
 * docs/plans/analytics.md PR AF, ruled d24: "Don't break the list, but put a
 * warning message that explains what was removed and how it impacted the
 * list").
 *
 * The SERVER decides it (⚑ d35): its compiler knows which conditions named
 * something missing and what each one did to the answer, and this module only
 * reads that and says it in words. It never guesses from the vocabulary on
 * screen — the builder's fields are the cards in view, not the compiler's.
 *
 * One entry per condition:
 *   { code: 'unresolved_name', names: [{ name, kind, card? }], term, negated, effect }
 * `term` is canonical KQL without its `not`; `negated` says it is read under
 * one; `effect` is what the CONDITION matches after its `not`s — not the
 * list: inside an `or`, the list shows what the other conditions match — so
 * every sentence says "the condition". `card` names the other collection's
 * card a dot-walk found the name missing from. A last `{ code: 'omitted',
 * count }` counts the entries the header had no room for.
 *
 * The server tells a name only to someone who could read it anyway (the AF
 * review): nothing to a caller who cannot read the collection's schema, a
 * collection they cannot open reads as one that does not exist, and a name
 * on another collection's card they cannot read is left out. So the words
 * are true either way: "isn't a collection you can open here".
 */
import { parse, serialize, isElementIdSegment, isBlock, blockOf } from './kql';
import { conditionValueLabel, describeCondition, describeFacetCondition, findField } from './kqlBuilder';
import { referenceValueLabel } from './filterSummary';

export const QUERY_WARNINGS_HEADER = 'x-query-warnings';
/** What the server read a schema reader's `q` as (deterministic filters F3). */
export const QUERY_CANONICAL_HEADER = 'x-query-canonical';
export const UNRESOLVED_NAME = 'unresolved_name';
export const OMITTED = 'omitted';
export const EFFECTS = ['matches_none', 'matches_all', 'left_out', 'cannot_answer'];
const KINDS = new Set(['element', 'lookup', 'card', 'set', 'facet', 'collection']);

const headerValue = (headers, name) => {
	if (!headers) return undefined;
	if (typeof headers.get === 'function') {
		const value = headers.get(name);
		if (value !== undefined && value !== null) return value;
	}
	const key = Object.keys(headers).find(candidate => candidate.toLowerCase() === name);
	return key === undefined ? undefined : headers[key];
};

const NONE = Object.freeze({ warnings: [], omitted: 0 });

/**
 * The text the list read its `q` as — `X-Query-Canonical`, percent-encoded
 * KQL with the names a schema reader typed resolved to ids — or null when the
 * response carries none or it does not decode.
 */
export function readCanonicalQuery(headers) {
	const raw = headerValue(headers, QUERY_CANONICAL_HEADER);
	if (typeof raw !== 'string' || !raw.trim()) return null;
	try {
		const text = decodeURIComponent(raw).trim();
		return text || null;
	} catch {
		return null;
	}
}

/**
 * What a response carries: `{ warnings, omitted }` — nothing when the header
 * is absent, empty, unreadable, or holds nothing this client understands; a
 * malformed value must never cost the list. Unknown codes, effects and kinds
 * are dropped, so a newer API can add to the channel without an older page
 * misreading it. `omitted` counts the entries the server had no room for.
 */
export function readQueryWarnings(headers) {
	const raw = headerValue(headers, QUERY_WARNINGS_HEADER);
	if (typeof raw !== 'string' || !raw.trim()) return NONE;
	let parsed;
	try {
		parsed = JSON.parse(decodeURIComponent(raw));
	} catch {
		return NONE;
	}
	if (!Array.isArray(parsed)) return NONE;
	const warnings = parsed
		.filter(entry => entry && entry.code === UNRESOLVED_NAME && typeof entry.term === 'string' && EFFECTS.includes(entry.effect))
		.map(entry => ({
			code: entry.code,
			term: entry.term,
			negated: entry.negated === true,
			effect: entry.effect,
			names: (Array.isArray(entry.names) ? entry.names : [])
				.filter(n => n && typeof n.name === 'string' && n.name && KINDS.has(n.kind))
				.map(n => ({
					name: n.name,
					kind: n.kind,
					...(typeof n.card === 'string' && n.card ? { card: n.card } : {}),
					// A deleted card's id, or a set no card here carries
					// (deterministic filters F2): "a removed card".
					...(n.removed === true ? { removed: true } : {}),
				})),
		}))
		.filter(entry => entry.names.length);
	const more = parsed.find(entry => entry && entry.code === OMITTED);
	const omitted = more && Number.isInteger(more.count) && more.count > 0 ? more.count : 0;
	return warnings.length || omitted ? { warnings, omitted } : NONE;
}

/** Just the warnings (see readQueryWarnings). */
export const parseQueryWarnings = headers => readQueryWarnings(headers).warnings;

/** A stable key for what a response said — two responses saying the same thing compare equal. */
export const queryWarningsKey = value => JSON.stringify(value || []);

/**
 * The canonical text of every condition in `query`, so a page can tell which
 * warnings are about the person's OWN filter (and offer to edit it) and which
 * came from somewhere they cannot edit here, such as a card's worklist filter.
 */
export function conditionTermsOf(query) {
	const text = `${query || ''}`.trim();
	if (!text) return new Set();
	let ast;
	try { ast = parse(text); } catch { return new Set(); }
	const out = new Set();
	const walk = node => {
		if (!node || typeof node !== 'object') return;
		if (node.op === 'cmp') {
			try { out.add(serialize(node)); } catch { /* unreadable: skip */ }
			return;
		}
		(node.args || []).forEach(walk);
		if (node.arg) walk(node.arg);
	};
	walk(ast);
	return out;
}

// System fields, as the chips and the builder name them.
const SYSTEM_LABELS = { card: 'Card', collection: 'Collection', set: 'Element set', created: 'Created', updated: 'Updated', notes: 'Notes', tags: 'Tags', id: 'Id' };

/** "A", "A and B", "A, B and C". */
export function joinNames(names) {
	const list = (names || []).filter(Boolean);
	if (list.length <= 1) return list[0] || '';
	return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/**
 * The condition in words, the way its chip reads — "Status is Open",
 * "not (Status is Open)" for one read under a `not`. Element names read as
 * their labels where the collection still has them, and as written where it
 * does not; a card as its name; a lookup id as its title when `titleFor`
 * knows it. Falls back to the raw text when it does not parse.
 */
export function describeTerm(warning, { fields = [], resolveCard = null, titleFor = null } = {}) {
	let node;
	try { node = parse(warning.term); } catch { node = null; }
	let text = warning.term;
	// A repeating group's block (keepr-api KPR-681): the group by its label,
	// what it asks of an entry as written.
	if (node && isBlock(node)) {
		const group = findField(fields, node.path, !!node.forceElement, false).label;
		text = `${group} has (${serialize(blockOf(node))})`;
	}
	else if (node && node.op === 'cmp') {
		const title = raw => (typeof titleFor === 'function' ? titleFor(raw) : undefined);
		const isSystem = (path, forceElement) => !forceElement && (path || []).length === 1 && SYSTEM_LABELS[path[0]] !== undefined;
		const labelFor = (path, forceElement, facet) => {
			if (!facet && isSystem(path, forceElement)) {
				// The builder's own descriptor when it has one ("Element set"),
				// else the chip's word — never the bare `card`.
				const own = (fields || []).find(field => field.key === path[0] && field.system);
				return own?.label || SYSTEM_LABELS[path[0]];
			}
			return findField(fields, path, forceElement, facet).label;
		};
		const removed = new Set((warning.names || []).filter(entry => entry.removed).map(entry => String(entry.name).toLowerCase()));
		const valueLabelFor = (path, forceElement, raw, facet) => {
			if (!facet && !forceElement && (path || [])[0] === 'card' && path.length === 1) {
				const card = typeof resolveCard === 'function' ? resolveCard(raw) : null;
				if (card?.name) return card.name;
			}
			// A card or set id, as its chip says it (filterSummary).
			const reference = referenceValueLabel(findField(fields, path, forceElement, facet), raw, removed);
			if (reference !== undefined) return reference;
			const supplied = title(raw);
			if (supplied !== undefined && supplied !== null && supplied !== '') return supplied;
			return conditionValueLabel(findField(fields, path, forceElement, facet), raw);
		};
		const refLabelFor = name => findField(fields, [name]).label;
		text = node.facet
			? describeFacetCondition(findField(fields, node.path, false, true), node, raw => valueLabelFor(node.path, false, raw, true))
			: describeCondition(node, labelFor, valueLabelFor, refLabelFor, n => findField(fields, n.path, n.forceElement, false));
	}
	return warning.negated ? `not (${text})` : text;
}

/** One gone name as the person knows it — the label or card name where there still is one, as written where not. */
function nameLabel({ name, kind, card }, { fields = [], resolveCard = null } = {}) {
	// Missing from another collection's card: this list's fields are not that
	// card's, so the name stays as written.
	if (card) return isElementIdSegment(name) ? null : name;
	if (kind === 'element' || kind === 'lookup') {
		// An element named by id (`#k7f3q2xa`, KPR-168) has no name to show
		// once nothing here holds it: null, and the sentence counts it.
		const field = findField(fields, [name]);
		if (field.goneElement) return null;
		return field.label || name;
	}
	if (kind === 'card' || kind === 'facet') {
		const card = typeof resolveCard === 'function' ? resolveCard(name) : null;
		return card?.name || name;
	}
	if (kind === 'set') {
		const setField = (fields || []).find(field => field.key === 'set');
		const choice = setField?.options?.choices?.find(c => c.value === name);
		return choice?.label || name;
	}
	return name;
}

/**
 * What a REMOVED id was, without its id (deterministic filters F2: the server
 * marks a deleted card's id, or a set no card here carries, `removed`). The
 * id is no name a person reads, so the sentence counts instead.
 */
function whatWasRemoved(kind, labels, card = null) {
	const n = labels.length;
	if (kind === 'elementId') return n > 1 ? `${n} elements it names are no longer ${card ? `on the ${card} card` : 'on any card here'}` : `An element it names is no longer ${card ? `on the ${card} card` : 'on any card here'}`;
	if (kind === 'set') return n > 1 ? `${n} element sets it names are no longer on any card here` : 'An element set it names is no longer on any card here';
	return n > 1 ? `${n} cards it names were deleted` : 'A card it names was deleted';
}

/**
 * "Status is no longer on any card here" — what is gone, in the kind's own
 * words. `card` is another collection's card a dot-walk looked in: the
 * sentence names it rather than saying "here".
 */
function whatIsGone(kind, labels, card) {
	const who = joinNames(labels);
	const many = labels.length > 1;
	switch (kind) {
		case 'lookup': return `${who} ${many ? 'are no longer item lookups' : 'is no longer an item lookup'}${card ? ` on the ${card} card` : ''}`;
		case 'card': return `${who} ${many ? 'are no longer cards' : 'is no longer a card'} here`;
		case 'set': return `${who} ${many ? 'are no longer element sets' : 'is no longer an element set'} here`;
		case 'facet': return `Nothing here points at ${who} any more`;
		// True whether it does not exist or the reader cannot open it — the
		// server reports both alike, and says nothing more.
		case 'collection': return `${who} ${many ? "aren't collections" : "isn't a collection"} you can open here`;
		case 'element':
		default: return `${who} ${many ? 'are' : 'is'} no longer ${card ? `on the ${card} card` : 'on any card here'}`;
	}
}

/**
 * One warning as the sentence the list shows — about the CONDITION, which is
 * what the server's effect describes (inside an `or`, the list still shows
 * what the rest of the filter matches):
 *   "Status is no longer on any card here, so the condition “status is open” matches no items."
 *   "…, so the condition “not (status is open)” matches every item."
 *   "Retired is no longer a card here, so it was left out of the condition “Card is any of Task, retired”."
 *   "…, so keepr can't answer the condition “On hand <= {{reorder-at}}”, and it matches no items."
 */
export function describeQueryWarning(warning, options = {}) {
	const groups = new Map(); // kind + card -> labels
	(warning.names || []).forEach(entry => {
		const label = nameLabel(entry, options);
		// An element id with no name to show is counted, as a removed id is.
		const byId = label === null;
		const kind = byId ? 'elementId' : entry.kind;
		const key = JSON.stringify([kind, entry.card || '', byId || !!entry.removed]);
		if (!groups.has(key)) groups.set(key, { kind, card: entry.card || null, removed: byId || !!entry.removed, labels: [] });
		groups.get(key).labels.push(label);
	});
	const joined = [...groups.values()].map(({ kind, labels, card, removed }) => (removed ? whatWasRemoved(kind, labels, card) : whatIsGone(kind, labels, card))).join('; ');
	// Sentence case: a name kept as written ("status") still opens with a
	// capital. The quoted condition reads exactly as its chip in the toolbar.
	const gone = joined.charAt(0).toUpperCase() + joined.slice(1);
	const term = `the condition “${describeTerm(warning, options)}”`;
	const count = (warning.names || []).length;
	switch (warning.effect) {
		case 'matches_all': return `${gone}, so ${term} matches every item.`;
		case 'left_out': return `${gone}, so ${count > 1 ? 'they were' : 'it was'} left out of ${term}.`;
		case 'cannot_answer': return `${gone}, so keepr can't answer ${term}, and it matches no items.`;
		case 'matches_none':
		default: return `${gone}, so ${term} matches no items.`;
	}
}

/** The line for what the header had no room for, or null: "2 more conditions name something that’s gone." */
export function describeOmitted(count) {
	if (!Number.isInteger(count) || count < 1) return null;
	return `${count} more ${count === 1 ? 'condition names' : 'conditions name'} something that’s gone.`;
}
